import React, { useState, useEffect } from 'react';
import { safeRedact } from '../../utils/redactCritical';
import toast from 'react-hot-toast';
import Modal from '../common/Modal';
import { getBackendApiUrl } from '../../config/backend';
import { logger } from '../../utils/logger';
import { chatSessionKeyHeaders } from '../../services/sessionKey';
import chatAgentService from '../../services/RealChatAgentService';
import styles from './SurveyModal.module.css';

interface FaceIconProps {
  value: string;
  selected: boolean;
}

interface SurveyModalProps {
  isOpen: boolean;
  onClose: () => void;
  userEmail?: string;
  sessionId?: string | null;
  getToken?: () => Promise<string | undefined>;
  partnerToken?: string | null;
}

interface SavedDraft {
  clarity: number | null;
  helpfulness: number | null;
  accuracy: number | null;
  feedback: string;
  savedAt: string;
}

/**
 * Face Icon Component for Ratings
 * Renders outlined face icons for rating scale (-1, 0, 1)
 */
const FaceIcon: React.FC<FaceIconProps> = ({ value, selected }) => {
  // Primary color from global styles (--color-primary)
  const primaryColor = '#2b7088';
  const faces: Record<string, React.ReactNode> = {
    '-1': (
      <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke={selected ? primaryColor : '#9ca3af'} strokeWidth="1.5">
        <circle cx="12" cy="12" r="10" />
        <circle cx="9" cy="9" r="1.5" fill={selected ? primaryColor : '#9ca3af'} />
        <circle cx="15" cy="9" r="1.5" fill={selected ? primaryColor : '#9ca3af'} />
        <path d="M9 15 Q12 13 15 15" strokeLinecap="round" />
      </svg>
    ),
    '0': (
      <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke={selected ? primaryColor : '#9ca3af'} strokeWidth="1.5">
        <circle cx="12" cy="12" r="10" />
        <circle cx="9" cy="9" r="1.5" fill={selected ? primaryColor : '#9ca3af'} />
        <circle cx="15" cy="9" r="1.5" fill={selected ? primaryColor : '#9ca3af'} />
        <line x1="9" y1="15" x2="15" y2="15" strokeLinecap="round" />
      </svg>
    ),
    '1': (
      <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke={selected ? primaryColor : '#9ca3af'} strokeWidth="1.5">
        <circle cx="12" cy="12" r="10" />
        <circle cx="9" cy="9" r="1.5" fill={selected ? primaryColor : '#9ca3af'} />
        <circle cx="15" cy="9" r="1.5" fill={selected ? primaryColor : '#9ca3af'} />
        <path d="M9 13 Q12 16 15 13" strokeLinecap="round" />
      </svg>
    ),
  };

  return <>{faces[value] || null}</>;
};

/**
 * SurveyModal - Feedback collection modal
 *
 * Features:
 * - Three required rating scales (clarity, helpfulness, accuracy) with face icons (-1, 0, 1)
 * - Optional freeform text input with 1000 character limit
 * - Auto-closing with progress bar after submission
 * - Collects timestamp, user email, and session ID
 */
// TRACI-710: what each face means, for screen readers (and the Inbox row says the same words).
const RATING_WORDS: Record<number, string> = { [-1]: 'poor', 0: 'okay', 1: 'good' };

const SurveyModal: React.FC<SurveyModalProps> = ({ isOpen, onClose, userEmail, sessionId: sessionIdProp, getToken, partnerToken }) => {
  // TRACI-710: file against the chat's live session id. The host passes the service's
  // field, which mutates without a re-render, so follow the service while open.
  const [liveSessionId, setLiveSessionId] = useState<string | null>(null);
  useEffect(() => {
    if (!isOpen) return;
    return chatAgentService.onAgentSessionIdChange(setLiveSessionId);
  }, [isOpen]);
  const sessionId = liveSessionId || sessionIdProp || null;
  const [clarity, setClarity] = useState<number | null>(null);
  const [helpfulness, setHelpfulness] = useState<number | null>(null);
  const [accuracy, setAccuracy] = useState<number | null>(null);
  const [feedback, setFeedback] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [isSuccess, setIsSuccess] = useState<boolean>(false);
  const [progress, setProgress] = useState<number>(100);

  const MAX_CHARS = 1000;
  const AUTO_CLOSE_DURATION = 5000; // 5 seconds
  const STORAGE_KEY = `survey_draft_${sessionId}`;

  // Load saved draft when modal opens
  useEffect(() => {
    if (isOpen && sessionId) {
      try {
        const savedDraft = localStorage.getItem(STORAGE_KEY);
        if (savedDraft) {
          const draft: SavedDraft = JSON.parse(savedDraft);
          setClarity(draft.clarity ?? null);
          setHelpfulness(draft.helpfulness ?? null);
          setAccuracy(draft.accuracy ?? null);
          setFeedback(draft.feedback ?? '');
        }
      } catch (err) {
        logger.error('Error loading survey draft:', err);
      }
    }
  }, [isOpen, sessionId, STORAGE_KEY]);

  // Save draft to localStorage whenever ratings or feedback change
  useEffect(() => {
    if (isOpen && sessionId && !isSuccess) {
      try {
        const draft: SavedDraft = {
          clarity,
          helpfulness,
          accuracy,
          feedback: safeRedact(feedback).text, // CPOC-140: no card number at rest in the browser
          savedAt: new Date().toISOString()
        };
        localStorage.setItem(STORAGE_KEY, JSON.stringify(draft));
      } catch (err) {
        logger.error('Error saving survey draft:', err);
      }
    }
  }, [clarity, helpfulness, accuracy, feedback, isOpen, sessionId, isSuccess, STORAGE_KEY]);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setIsSubmitting(false);
      setIsSuccess(false);
      setProgress(100);
      // Clear form state to prevent re-saving to localStorage
      setClarity(null);
      setHelpfulness(null);
      setAccuracy(null);
      setFeedback('');
    }
  }, [isOpen]);

  // Handle auto-close after successful submission
  useEffect(() => {
    if (isSuccess) {
      const startTime = Date.now();
      const interval = setInterval(() => {
        const elapsed = Date.now() - startTime;
        const remaining = Math.max(0, 100 - (elapsed / AUTO_CLOSE_DURATION) * 100);
        setProgress(remaining);

        if (remaining === 0) {
          clearInterval(interval);
          onClose();
        }
      }, 50);

      return () => clearInterval(interval);
    }
  }, [isSuccess, onClose]);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();

    // Validate required ratings
    if (clarity === null || helpfulness === null || accuracy === null) {
      toast.error('Please provide all three ratings before submitting.');
      return;
    }

    // Validate feedback length if provided
    if (feedback.length > MAX_CHARS) {
      toast.error(`Feedback must be ${MAX_CHARS} characters or less.`);
      return;
    }

    // TRACI-710: feedback is filed against the chat, so there has to be one.
    if (!sessionId) {
      toast.error('Send a message first, then tell us how the chat went.');
      return;
    }
    setIsSubmitting(true);

    try {
      // Build headers with authentication
      // TRACI-710: the backend files only for the chat's owner, proven by its session key.
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        ...chatSessionKeyHeaders(sessionId),
      };

      // Add authentication header
      if (getToken) {
        const token = await getToken();
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }
      } else if (partnerToken) {
        headers['Authorization'] = `Bearer ${partnerToken}`;
      }

      const response = await fetch(`${getBackendApiUrl()}/api/survey/feedback`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          clarity,
          helpfulness,
          accuracy,
          feedback: safeRedact(feedback.trim()).text || null,
          userEmail: userEmail || 'anonymous',
          sessionId,
          timestamp: new Date().toISOString(),
        }),
      });

      if (!response.ok) {
        // TRACI-710: 404 = the form was switched off; 429 = this chat already sent its share.
        const message =
          response.status === 404 ? 'Feedback is not being collected right now. Thanks for trying!'
          : response.status === 429 ? 'Thanks, we already have your feedback from this chat.'
          : 'Failed to submit feedback. Please try again.';
        toast.error(message);
        setIsSubmitting(false);
        return;
      }

      setIsSuccess(true);

      // Clear the saved draft from localStorage on successful submission
      try {
        localStorage.removeItem(STORAGE_KEY);
      } catch (err) {
        logger.error('Error clearing survey draft:', err);
      }
    } catch (err) {
      logger.error('Error submitting feedback:', err);
      toast.error('Failed to submit feedback. Please try again.');
      setIsSubmitting(false);
    }
  };

  const remainingChars = MAX_CHARS - feedback.length;
  const isOverLimit = remainingChars < 0;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="We Would Love Your Feedback!">
      {isSuccess ? (
        <div className={styles.successContainer}>
          <div className={styles.successIcon}>
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
              <circle cx="12" cy="12" r="10" stroke="#10b981" strokeWidth="2" fill="none" />
              <path d="M8 12l2 2 4-4" stroke="#10b981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
          <h3 className={styles.successTitle}>Thank you for your feedback!</h3>
          <p className={styles.successMessage}>
            Your response has been submitted successfully.
          </p>
          <div className={styles.progressBarContainer}>
            <div
              className={styles.progressBar}
              style={{ width: `${progress}%` }}
            />
          </div>
          <p className={styles.autoCloseText}>This window will close automatically...</p>
          {sessionId && (
            <p className={styles.sessionId}>Session ID: {sessionId}</p>
          )}
        </div>
      ) : (
        <form onSubmit={handleSubmit} className={styles.form}>
          {/* Rating Groups */}
          <div className={styles.ratingsSection}>
            <p className={styles.ratingsTitle}>Please rate your experience:</p>

            {/* Clarity Rating */}
            <div className={styles.ratingGroup}>
              <label className={styles.ratingLabel}>Clarity</label>
              <div className={styles.ratingOptions}>
                {[-1, 0, 1].map((value) => (
                  <button
                    key={value}
                    type="button"
                    onClick={() => setClarity(clarity === value ? null : value)}
                    aria-label={`Clarity: ${RATING_WORDS[value]}`}
                    aria-pressed={clarity === value}
                    className={`${styles.ratingButton} ${clarity === value ? styles.ratingButtonSelected : ''}`}
                    disabled={isSubmitting}
                  >
                    <FaceIcon value={value.toString()} selected={clarity === value} />
                  </button>
                ))}
              </div>
            </div>

            {/* Helpfulness Rating */}
            <div className={styles.ratingGroup}>
              <label className={styles.ratingLabel}>Helpfulness</label>
              <div className={styles.ratingOptions}>
                {[-1, 0, 1].map((value) => (
                  <button
                    key={value}
                    type="button"
                    onClick={() => setHelpfulness(helpfulness === value ? null : value)}
                    aria-label={`Helpfulness: ${RATING_WORDS[value]}`}
                    aria-pressed={helpfulness === value}
                    className={`${styles.ratingButton} ${helpfulness === value ? styles.ratingButtonSelected : ''}`}
                    disabled={isSubmitting}
                  >
                    <FaceIcon value={value.toString()} selected={helpfulness === value} />
                  </button>
                ))}
              </div>
            </div>

            {/* Accuracy Rating */}
            <div className={styles.ratingGroup}>
              <label className={styles.ratingLabel}>Accuracy</label>
              <div className={styles.ratingOptions}>
                {[-1, 0, 1].map((value) => (
                  <button
                    key={value}
                    type="button"
                    onClick={() => setAccuracy(accuracy === value ? null : value)}
                    aria-label={`Accuracy: ${RATING_WORDS[value]}`}
                    aria-pressed={accuracy === value}
                    className={`${styles.ratingButton} ${accuracy === value ? styles.ratingButtonSelected : ''}`}
                    disabled={isSubmitting}
                  >
                    <FaceIcon value={value.toString()} selected={accuracy === value} />
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Optional Text Feedback */}
          <div className={styles.feedbackSection}>
            <label htmlFor="feedback" className={styles.label}>
              Additional Comments <span className={styles.optionalLabel}>(optional)</span>
            </label>
            <textarea
              id="feedback"
              value={feedback}
              onChange={(e) => setFeedback(e.target.value)}
              className={`${styles.textarea} ${isOverLimit ? styles.textareaError : ''}`}
              placeholder="Share your thoughts, suggestions, or concerns..."
              rows={4}
              disabled={isSubmitting}
            />
            <div className={styles.footer}>
              <span className={`${styles.charCount} ${isOverLimit ? styles.charCountError : ''}`}>
                {remainingChars} characters remaining
              </span>
            </div>
          </div>

          <button
            type="submit"
            disabled={isSubmitting || isOverLimit || clarity === null || helpfulness === null || accuracy === null}
            className={styles.submitButton}
          >
            {isSubmitting ? (
              <>
                <div className={styles.spinner} />
                Sending...
              </>
            ) : (
              'Submit'
            )}
          </button>

          {sessionId && (
            <p className={styles.sessionId}>Session ID: {sessionId}</p>
          )}
        </form>
      )}
    </Modal>
  );
};

export default SurveyModal;
